import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import type { IconGeneratorStatus } from '../hooks/useIconGenerator';

interface IconGeneratorFormProps {
  status: IconGeneratorStatus;
  onGenerate: (name: string, description: string, useDefaultStyle: boolean, style?: string) => void;
  onSave: (fileName?: string) => void;
  onReset: () => void;
}

export function IconGeneratorForm({ status, onGenerate, onSave, onReset }: IconGeneratorFormProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [useDefaultStyle, setUseDefaultStyle] = useState(true);
  const [style, setStyle] = useState('');

  const isGenerating = status === 'generating';
  const isSaving = status === 'saving';
  const canGenerate =
    name.trim().length > 0 && description.trim().length > 0 && !isGenerating && !isSaving;
  const canSave = status === 'generated';

  const handleGenerate = () => {
    if (!canGenerate) return;
    onGenerate(name.trim(), description.trim(), useDefaultStyle, style.trim() || undefined);
  };

  const handleSave = () => {
    if (!canSave) return;
    const safeName = name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    onSave(safeName ? `${safeName}-icon` : 'icon');
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="icon-name">Name</Label>
        <Input
          id="icon-name"
          placeholder="e.g., Chat, Calendar, Marketplace"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          disabled={isGenerating}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="icon-description">Description</Label>
        <Textarea
          id="icon-description"
          placeholder="e.g., Real-time messaging and channels for teams"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          rows={3}
          disabled={isGenerating}
        />
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="use-default-style"
          checked={useDefaultStyle}
          onCheckedChange={(checked) => setUseDefaultStyle(checked === true)}
          disabled={isGenerating}
        />
        <Label htmlFor="use-default-style" className="text-sm font-normal cursor-pointer">
          Use default Fias style (aurora gradient)
        </Label>
      </div>

      {!useDefaultStyle && (
        <div className="space-y-2">
          <Label htmlFor="icon-style">Custom Style</Label>
          <Textarea
            id="icon-style"
            placeholder="e.g., Warm sunset orange gradient with golden accents"
            value={style}
            onChange={(e) => setStyle(e.target.value)}
            maxLength={500}
            rows={2}
            disabled={isGenerating}
          />
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={handleGenerate} disabled={!canGenerate}>
          {isGenerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isGenerating ? 'Generating...' : 'Generate'}
        </Button>

        {canSave && (
          <Button onClick={handleSave} variant="secondary">
            Save to Files
          </Button>
        )}

        {isSaving && (
          <Button disabled variant="secondary">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Saving...
          </Button>
        )}

        {(status === 'generated' || status === 'saved' || status === 'error') && (
          <Button onClick={onReset} variant="outline">
            Reset
          </Button>
        )}
      </div>
    </div>
  );
}
